/**
 * FoodPulseTableCard — printable "table card" for restaurants.
 *
 * Route: /food-pulse/businesses/:slug/table-card
 *
 * A 4x6 (portrait) page-per-card layout that combines:
 *   - business cover photo (from cached photo enrichment)
 *   - business name + tagline
 *   - large QR code that opens the FoodPulse business page
 *   - short URL underneath in a monospace band
 *
 * The print stylesheet strips nav / buttons and forces one card per
 * page with clean margins so it's ready to laminate straight off the
 * printer. Restaurants use these on tables, take-out packaging, and
 * shop windows.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Printer, Loader2, RefreshCw } from "lucide-react";
import { http } from "@/lib/api";
import { normalizeShortUrl, normalizeAssetUrl, synthesizeQrFromShortUrl } from "@/lib/splinkUrl";

const GREEN  = "#0F3D2E";
const CREAM  = "#FBF7EF";
const AMBER  = "#E4A140";
const PURPLE = "#7C3AED";

export default function FoodPulseTableCard() {
  const { slug } = useParams();
  const [biz, setBiz]     = useState(null);
  const [photo, setPhoto] = useState(null);
  const [splink, setSplink] = useState(null);
  const [busy, setBusy]   = useState(true);
  const [count, setCount] = useState(1);   // how many copies to print

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    (async () => {
      try {
        const b = await http.get(`/food-businesses/${slug}`);
        if (cancelled) return;
        setBiz(b.data);
        const longUrl = (typeof window !== "undefined" ? window.location.origin : "https://foodpulse.net")
          + `/food-pulse/businesses/${slug}`;
        const [ph, sp] = await Promise.all([
          http.get(`/food-businesses/${slug}/photo`).catch(() => null),
          http.post(`/splink/for-page`, { long_url: longUrl, label: b.data.name, site: "foodpulse" }).catch(() => null),
        ]);
        if (cancelled) return;
        setPhoto(ph?.data?.photo_url || null);
        setSplink(sp?.data || null);
      } catch (e) { console.warn(e); }
      if (!cancelled) setBusy(false);
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (busy) return <div className="p-10 text-center"><Loader2 className="mx-auto animate-spin" /></div>;
  if (!biz) return <div className="p-10 text-center">Business not found.</div>;

  const shortUrl = normalizeShortUrl(splink?.short_url);
  const qrUrl    = normalizeAssetUrl(
    splink?.qr_code_url || synthesizeQrFromShortUrl(shortUrl),
    process.env.REACT_APP_BACKEND_URL || "",
  );
  const address  = [biz.address_line1, biz.city, biz.state].filter(Boolean).join(", ");
  const copies   = Array.from({ length: Math.max(1, Math.min(count, 20)) });

  return (
    <main className="min-h-screen" style={{ background: CREAM }} data-testid="table-card-page">
      {/* Print stylesheet — one card per page, no chrome */}
      <style>{`
        @media print {
          header, footer, .no-print { display: none !important; }
          body, main { background: white !important; }
          .card { page-break-after: always; break-after: page; margin: 0 !important; }
          .card:last-child { page-break-after: auto; }
          @page { size: A5 portrait; margin: 14mm; }
        }
      `}</style>

      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="no-print flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <Link to={`/food-pulse/businesses/${slug}`} className="inline-flex items-center gap-1 text-xs font-semibold mb-2" style={{ color: GREEN }} data-testid="back-to-business">
              <ArrowLeft size={12} /> Back to {biz.name}
            </Link>
            <h1 className="text-2xl font-black" style={{ color: GREEN }}>Table Card · Printable</h1>
            <p className="text-sm text-gray-600">Print one for every table, take-out bag, and shop window.</p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-700 inline-flex items-center gap-1">
              Copies
              <input
                type="number"
                min={1}
                max={20}
                value={count}
                onChange={(e) => setCount(Number(e.target.value) || 1)}
                className="w-16 border rounded-full px-2 py-1 text-sm"
                data-testid="copies-input"
              />
            </label>
            <button onClick={() => window.print()} className="rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-2" style={{ background: GREEN, color: CREAM }} data-testid="print-btn">
              <Printer size={16} /> Print / Save as PDF
            </button>
          </div>
        </div>

        {copies.map((_, i) => (
          <article
            key={i}
            className="card rounded-3xl border overflow-hidden bg-white shadow-sm mb-6"
            style={{ borderColor: "#E9D8B4" }}
            data-testid={`table-card-${i}`}
          >
            {/* Cover photo strip */}
            {photo ? (
              <img src={photo} alt={biz.name} className="w-full h-40 object-cover" />
            ) : (
              <div className="w-full h-40 flex items-center justify-center" style={{ background: "linear-gradient(135deg,#F0E2C7,#FEF3C7)" }}>
                <div className="text-4xl font-black" style={{ color: AMBER }}>{(biz.name || "?").slice(0, 1)}</div>
              </div>
            )}

            <div className="p-6 flex flex-col md:flex-row gap-6 items-center">
              {/* QR block */}
              <div className="flex-shrink-0 rounded-2xl bg-white p-3 border" style={{ borderColor: "#E9D8B4" }}>
                {qrUrl
                  ? <img src={qrUrl} alt="QR" width={168} height={168} className="block" data-testid={`table-card-qr-${i}`} />
                  : <div className="w-[168px] h-[168px] flex items-center justify-center text-gray-400">No QR</div>}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 text-center md:text-left">
                <div className="text-[11px] uppercase tracking-widest font-bold" style={{ color: PURPLE }}>
                  Scan · Explore · Share
                </div>
                <h2 className="text-3xl md:text-4xl font-black leading-tight mt-1" style={{ color: GREEN }}>
                  {biz.name}
                </h2>
                {address && <div className="text-sm text-gray-700 mt-1">{address}</div>}
                {biz.tagline && <p className="text-sm mt-3 italic text-gray-600">"{biz.tagline}"</p>}
                {shortUrl && (
                  <div className="mt-4 inline-flex items-center gap-2 rounded-full px-4 py-2" style={{ background: "#FEF3C7" }}>
                    <span className="text-[11px] uppercase tracking-widest font-bold" style={{ color: GREEN }}>Link</span>
                    <span className="font-mono text-sm" style={{ color: GREEN }}>
                      {shortUrl.replace(/^https?:\/\//, "")}
                    </span>
                  </div>
                )}
                <div className="mt-3 text-[11px] uppercase tracking-widest text-gray-500">
                  Powered by FoodPulse · foodpulse.net
                </div>
              </div>
            </div>
          </article>
        ))}
      </div>
    </main>
  );
}
